<script setup>
// Секция для отчёта «Каналы»: «Нагрузка по каналам» (диалоги по каналам, %) и
// «Клиенты по системам» (уникальные клиенты по источникам). Самодостаточна:
// тянет AnalyticsAPI.overview за 30 дней. Р19 п.2/3: оба среза сведены сюда,
// чтобы не дублировать «по системам» в отчёте ИИ-агентов и чтобы было понятно,
// почему числа разные — это ДИАЛОГИ против уникальных КЛИЕНТОВ.
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BarRow from 'dashboard/components-next/analytics/BarRow.vue';
import AnalyticsAPI from 'dashboard/api/analytics';

const { t } = useI18n();

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const PERIOD_DAYS = 30;

const CHANNEL_KEYS = {
  telegram: 'ANALYTICS.CHANNELS.TELEGRAM',
  max: 'ANALYTICS.CHANNELS.MAX',
  vk: 'ANALYTICS.CHANNELS.VK',
  email: 'ANALYTICS.CHANNELS.EMAIL',
  website: 'ANALYTICS.CHANNELS.WEBSITE',
  // by_channel демодулизует Channel::WebWidget → web_widget; показываем «Сайт».
  web_widget: 'ANALYTICS.CHANNELS.WEBSITE',
  api: 'ANALYTICS.CHANNELS.API',
  sms: 'ANALYTICS.CHANNELS.SMS',
  whatsapp: 'ANALYTICS.CHANNELS.WHATSAPP',
};

const isFetching = ref(false);
const overview = ref(null);

const channelLabel = channel =>
  CHANNEL_KEYS[channel] ? t(CHANNEL_KEYS[channel]) : channel;

// Диалоги по каналам (с долей %).
const channelRows = computed(() => {
  const byChannel = overview.value?.by_channel || {};
  const entries = Object.entries(byChannel);
  const total = entries.reduce((acc, [, value]) => acc + value, 0);
  const max = entries.reduce((acc, [, value]) => Math.max(acc, value), 0);
  return entries
    .map(([channel, value]) => ({
      channel,
      value,
      max,
      pct: total > 0 ? Math.round((value / total) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value);
});

// Уникальные клиенты по системам/источникам.
const systemRows = computed(() => {
  const bySystem = overview.value?.by_system || {};
  const entries = Object.entries(bySystem);
  const max = entries.reduce((acc, [, value]) => Math.max(acc, value), 0);
  return entries
    .map(([channel, value]) => ({ channel, value, max }))
    .sort((a, b) => b.value - a.value);
});

const fetchChannels = async () => {
  isFetching.value = true;
  try {
    const until = new Date();
    const since = new Date(until.getTime() - PERIOD_DAYS * DAY_IN_MS);
    const response = await AnalyticsAPI.overview({
      since: since.toISOString(),
      until: until.toISOString(),
    });
    overview.value = response.data;
  } catch {
    overview.value = null;
  } finally {
    isFetching.value = false;
  }
};

onMounted(() => fetchChannels());
</script>

<template>
  <section class="flex flex-col gap-6 pt-6 mt-6 border-t border-n-weak">
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-6 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else>
      <div class="flex flex-col gap-3">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('ANALYTICS.CHANNELS.TITLE') }}
        </h3>
        <div v-if="channelRows.length" class="flex flex-col gap-3">
          <BarRow
            v-for="row in channelRows"
            :key="row.channel"
            :label="channelLabel(row.channel)"
            :value="row.value"
            :max="row.max"
            :display-value="`${row.value} (${row.pct}%)`"
          />
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ t('ANALYTICS.EMPTY') }}
        </p>
      </div>

      <div class="flex flex-col gap-3">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('ANALYTICS.CHANNELS.CLIENTS_BY_SYSTEM') }}
        </h3>
        <p class="-mt-1 text-xs text-n-slate-11">
          {{ t('ANALYTICS.CHANNELS.CLIENTS_HINT') }}
        </p>
        <div v-if="systemRows.length" class="flex flex-col gap-3">
          <BarRow
            v-for="row in systemRows"
            :key="row.channel"
            :label="channelLabel(row.channel)"
            :value="row.value"
            :max="row.max"
          />
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ t('ANALYTICS.EMPTY') }}
        </p>
      </div>
    </template>
  </section>
</template>
